import React from 'react';
import { Logo } from '../../atom/logo/Logo';
import { useFooter } from '../../../hooks/useFooter';
import { Link } from 'gatsby';
import { Paragraph } from '../../atom/paragraph/Paragraph';
import './style.css';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { EarlypaySymbolLogo } from '../../atom/logo/EarlypayLogo';
import clsx from 'clsx';

const date = new Date();

export const Footer = ({ limited = false }: { limited?: boolean }) => {
	const { footerLinks, locations } = useFooter();

	const headings: string[] = [
		'What We Do',
		'Company',
		'Integrations',
		'Contact',
		'Resources',
	];

	const columns = headings.map((heading: string) => {
		return {
			title: heading,
			list: footerLinks.filter(
				(label: { column: string }) => label.column === heading
			),
		};
	});

	const privacyAndTerms = [
		{
			path: '/privacy-policy/',
			title: 'Privacy Policy',
		},
		{
			path: '/terms-and-conditions/',
			title: 'Terms & Conditions',
		},
	];

	const findColumn = (name: string) => {
		return columns.filter((column) => column.title === name)[0];
	};

	if (!columns) {
		return;
	}

	return (
		<footer className={'section flex-col bg-neutral-100'}>
			{!limited ? (
				<div className={'footer__wrapper'}>
					<Link to="/" className={'mb-10 mr-12'}>
						<VisuallyHidden>Earlypay Symbol Logo</VisuallyHidden>
						<EarlypaySymbolLogo height={40} />
					</Link>
					<ul className={'footer__menu'}>
						<FooterColumn {...findColumn('What We Do')} />
						<li>
							<ul>
								<FooterColumn {...findColumn('Company')} />
								<FooterColumn
									{...findColumn('Resources')}
									linkTitle="/resources"
								/>
							</ul>
						</li>
						<li>
							<ul>
								<FooterColumn
									{...findColumn('Integrations')}
									linkTitle="/integrations"
								/>
								<FooterColumn {...findColumn('Contact')} />
							</ul>
						</li>
						<AddressColumn locations={locations} />
					</ul>
				</div>
			) : null}
			<div
				className={clsx({
					footer__copyright: !limited,
					'max-w-[1200px] flex justify-between w-full': limited,
				})}
			>
				<Paragraph className="text-fineprint text-neutral-600 text-sm mb-0">
					&#169; {date.getFullYear()} All rights reserved.
				</Paragraph>

				<ul className={'flex flex-col mb-6 md:mb-0 md:flex-row'}>
					{privacyAndTerms.map(({ path, title }) => (
						<li className={'mb-8 md:ml-4 md:mb-0'} key={path}>
							<FooterLink name={title} link={path} />
						</li>
					))}
				</ul>
			</div>
		</footer>
	);
};

type AddressColumnType = {
	locations: {
		addressLine1: string;
		addressLine2: string;
		addressLine3: string;
	}[];
};

const AddressColumn = ({ locations }: AddressColumnType) => {
	return (
		<li className={'flex flex-col mb-2'}>
			<Paragraph className="font-semibold text-fineprint mb-2">
				Addresses
			</Paragraph>
			<ul>
				{locations.map((location, i) => (
					<li className={'mb-4'} key={location.addressLine1}>
						<p className={'text-sm font-medium text-ep-navy mb-1'}>
							{location.addressLine1}
						</p>
						<p
							className={
								'text-fineprint text-ep-navy mb-0 leading-relaxed font-light'
							}
						>
							{location.addressLine2}
						</p>
						{location.addressLine3 ? (
							<p
								className={
									'text-fineprint text-ep-navy mb-0 leading-relaxed font-light'
								}
							>
								{location.addressLine3}
							</p>
						) : null}
					</li>
				))}
			</ul>
		</li>
	);
};

const FooterColumn = ({
	title,
	linkTitle,
	list,
}: {
	title: string;
	linkTitle?: string;
	list: { name: string; link: string }[];
}) => (
	<li className={'flex flex-col mb-8'}>
		<Paragraph className="font-medium text-fineprint mb-2">
			{linkTitle ? (
				<Link to={linkTitle} className="text-fineprint">
					{title}
				</Link>
			) : (
				title
			)}
		</Paragraph>
		<ul>
			{list.map(({ name, link }) => (
				<li className={'mb-2'} key={link}>
					<FooterLink name={name} link={link} key={name} />
				</li>
			))}
		</ul>
	</li>
);

const FooterLink = ({ name, link }: { name: string; link: string }) => {
	const isGatsbyLink = link.startsWith('/');

	if (!isGatsbyLink) {
		return (
			<a href={link} className={'text-fineprint mb-0 text-ep-navy font-light'}>
				{name}
			</a>
		);
	}

	return (
		<Link to={link} className={'text-fineprint mb-0 text-ep-navy font-light'}>
			{name}
		</Link>
	);
};
